import Cookies from "js-cookie";
import React, { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import SideMenu from "../components/SideMenu";
import { useRefreshToken } from "../hooks/mutations/login-mutation";
import XuronLogo from "../img/logo2.svg";
import bg from "../img/mainbg.jpg";
import { useAuth } from "../context/AuthContext";

const SIDEBAR_WIDTH = 265;

const RootLayoutSigned = () => {
  const navigate = useNavigate();
  const { updateAuth, clearAuth } = useAuth();
  const location = useLocation();
  const outletRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const [mobileOpen, setMobileOpen] = useState(false);

  const refreshTokenMutation = useRefreshToken({
    onSuccess: (res) => {
      clearAuth();
      Cookies.set("access", res?.access as string, { expires: 1 });
      Cookies.set("refresh", res?.refresh as string, { expires: 30 });
      Cookies.set("role", res?.user?.role as string, { expires: 30 });
      Cookies.set("org_id", res?.user?.org_id?.toString() as string, {
        expires: 30,
      });
      Cookies.set("user_id", res?.user?.id?.toString() as string, {
        expires: 30,
      });
      Cookies.set("first_name", res?.user?.firstName?.toString() as string, {
        expires: 30,
      });
      Cookies.set("last_name", res?.user?.lastName?.toString() as string, {
        expires: 30,
      });
      Cookies.set("email", res?.user?.email?.toString() as string, {
        expires: 30,
      });
      updateAuth(
        res.user.role as any,
        String(res.user.org_id),
        String(res.user.org_name),
        String(res.user.id),
        res.user.firstName,
        res.user.lastName,
        String(res.user.email),
        true
      );
      const backto = searchParams.get("backto");
      if (backto) {
        navigate(decodeURIComponent(backto).replace(/^\/+/, "/"), { replace: true });
      } else {
        navigate("/home");
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  useEffect(() => {
    const accessToken = Cookies.get("access");
    const refreshToken = Cookies.get("refresh");

    if (refreshToken && !accessToken) {
      refreshTokenMutation.mutate({ refreshToken });
    }
  }, [navigate, location.pathname]);

  // Close drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // ESC to close mobile drawer
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setMobileOpen(false);
    if (mobileOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden">
      {/* Mobile top bar */}
      <header className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3 lg:hidden">
        <button
          aria-label="Open menu"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen(true)}
          className="rounded-lg p-2 hover:bg-gray-100 active:scale-95 transition"
        >
          {/* Hamburger */}
          <svg viewBox="0 0 24 24" className="h-6 w-6 text-gray-700" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        <div className="flex items-center gap-2">
          <img src={XuronLogo} alt="Xuron" className="h-5 w-auto" />
        </div>

        {/* spacer to balance flex */}
        <div className="w-10" />
      </header>

      <div className="relative flex min-h-0 flex-1">
        {/* Desktop sidebar */}
        <aside
          className="hidden h-full shrink-0 border-r border-gray-200 bg-white lg:block"
          style={{ width: SIDEBAR_WIDTH }}
        >
          <SideMenu />
        </aside>

        {/* --- Animated mobile overlay & drawer (always mounted for smooth transitions) --- */}
        {/* Overlay fades */}
        <div
          onClick={() => setMobileOpen(false)}
          className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-[1px] lg:hidden
            transition-opacity duration-300 ease-out motion-reduce:transition-none
            ${mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        />

        {/* Drawer slides */}
        <aside
          role="dialog"
          aria-modal="true"
          className={`fixed inset-y-0 left-0 z-50 w-[80vw] max-w-[320px] overflow-y-auto border-r border-gray-200 bg-white shadow-xl lg:hidden
            transform transition-transform duration-300 ease-out will-change-transform motion-reduce:transition-none
            ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
        >
          <SideMenu onNavigate={() => setMobileOpen(false)} />
        </aside>

        {/* Main content */}
        <main className={`min-w-0 flex-1 overflow-hidden`}>
          <div
            ref={outletRef}
            data-scroll-root
            className="h-full overflow-y-auto pb-4"
            style={{
              backgroundImage: location.pathname === "/home" ? `url(${bg})` : "none",
              backgroundColor: location.pathname === "/home" ? "transparent" : "white",
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            {/* page padding; tighter on phones */}
            <div className="px-4 py-5 sm:px-5 bg-contain max-md:bg-cover">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default RootLayoutSigned;
